import addressApi, {
  MUTATION_SYNC_PROPERTIES,
  QUERY_GET_PROPERTIES,
  QUERY_SYNC_PROPERTY_STATUS
} from "api/landlord/address";
import { useCallback, useEffect, useMemo, useState } from "react";

import { getErrorMessage } from "utils/error";
import { AxiosError } from "axios";
import { InputSize } from "shared/components/forms/Input";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components/IconButton";
import { Show } from "shared/components/helpers/Show";
import { BaseInput } from "shared/components/forms/BaseInput";
import { Layout } from "providers/Layout";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { DeletePropertyModal } from "shared/app/DeletePropertyModal";
import { PropertiesTable } from "./components/PropertiesTable";
import { AddressValidationIssuesCard } from "./components/AddressValidationIssuesCard";
import { LayoutButtons } from "./components/LayoutButtons";
import { ValidateAllAddressesButton } from "./components/ValidateAllAddressesButton";
import { Pagination } from "shared/components/Pagination";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { PropertyManagementSystemSync } from "shared/components/PropertyManagementSystemSync";
import {
  PropertyManagementSystemSyncStatus,
  propertyKindLabel
} from "shared/types";
import { useAuthContext } from "providers/Auth";
import { toast } from "react-toastify";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { FilterBadge } from "shared/app/LeadsTableView/components/FilterBadge";
import { FilterIcon } from "@hugeicons-pro/core-stroke-rounded";
import { PropertiesFiltersDrawer } from "./components/FiltersDrawer";
import { PropertiesFilters } from "./types";
import { usePropertiesQueryParams } from "./hooks/usePropertiesQueryParams";

const DEFAULT_PER_PAGE = Number(window.localStorage.getItem("per_page")) || 10;

export const Properties = () => {
  const dictionary = useUnitsPropertiesDictionary();
  const { type } = useAuthContext();
  const [showDeletePropertyModal, setShowDeletePropertyModal] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(
    null
  );
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const {
    search,
    filters,
    activeFilterCount,
    setSearch,
    setFilters,
    removeState,
    removePropertyType,
    removeScreeningTemplate,
    removeListingTemplate
  } = usePropertiesQueryParams();

  const applyFilters = useCallback(
    (next: PropertiesFilters) => {
      setFilters(next);
      setPage(1);
    },
    [setFilters]
  );

  const removeFilter = useCallback(<T,>(remove: (value: T) => void) => {
    return (value: T) => {
      remove(value);
      setPage(1);
    };
  }, []);

  const [isSyncing, setIsSyncing] = useState(type.isPMS);
  const [debouncedProperty] = useDebouncedValue(search, 500);

  const { data, refetch, isFetching, error } = useQuery({
    queryKey: [
      QUERY_GET_PROPERTIES,
      page,
      debouncedProperty,
      filters.states,
      filters.propertyTypes,
      filters.screeningTemplates,
      filters.listingTemplates
    ],
    queryFn: () =>
      addressApi.getProperties({
        page,
        searchTerm: debouncedProperty,
        per_page: DEFAULT_PER_PAGE,
        states: filters.states,
        kinds: filters.propertyTypes,
        verification_template_identifiers: filters.screeningTemplates?.map(
          (t) => t.id
        ),
        listing_template_identifiers: filters.listingTemplates?.map((t) => t.id)
      }),
    placeholderData: keepPreviousData
  });

  const { data: syncData, isError: syncError } =
    useQuery<PropertyManagementSystemSyncStatus>({
      queryKey: [QUERY_SYNC_PROPERTY_STATUS],
      queryFn: () =>
        addressApi.getPropertyManagementSystemSyncStatus("properties"),
      refetchInterval: 3000,
      enabled: isSyncing
    });

  const { mutate: handleRequestSync } = useMutation({
    mutationFn: async () => {
      return addressApi
        .requestPropertyManagementSystemSync("properties")
        .then(() => {
          toast.success("Sync properties requested successfully");
          setIsSyncing(true);
        })
        .catch((error) => {
          toast.error(getErrorMessage(error));
          setIsSyncing(false);
        });
    },
    mutationKey: [MUTATION_SYNC_PROPERTIES]
  });

  useEffect(() => {
    setPage(1);
  }, [debouncedProperty]);

  useEffect(() => {
    setIsSyncing(type.isPMS);
  }, [type.isPMS]);

  useEffect(() => {
    const isSyncRunning = syncData?.status === "running";
    setIsSyncing(isSyncRunning);

    if (!isSyncRunning) refetch();
  }, [syncData, syncError]);

  const filterErrorMessage = useMemo(() => {
    if (!error) return null;

    const status = (error as AxiosError)?.response?.status;
    if (status === 422)
      return "That combination matches too many properties. Narrow the filters and try again.";
    if (status === 503)
      return "Property search is temporarily unavailable. Try again in a moment.";

    return getErrorMessage(error);
  }, [error]);

  const hasAnyFilter = Boolean(debouncedProperty) || activeFilterCount > 0;

  return (
    <Layout
      footer={
        <div className="md:hidden flex ml-auto shrink-0 gap-2 md:w-auto w-full px-4 md:pl-4">
          <LayoutButtons onPropertyAdded={refetch} />
        </div>
      }
      hideFooterForDesktop
    >
      <div className="animate-appear">
        <div className="pb-4">
          <p className="m-0 mb-6 text-2xl font-bold text-content-1000">
            {dictionary.Properties}
          </p>
          <div className="flex w-full gap-2 flex-wrap">
            <BaseInput
              id="search"
              placeholder={`Search ${dictionary.properties}`}
              value={search}
              onChange={setSearch}
              className="!mb-0 w-full md:w-auto lg:min-w-[280px] max-w-[376px] h-12 md:h-10"
              size={InputSize.SM}
            />
            <IconButton
              aria-label="Filters"
              variant={IconButtonVariant.BASE}
              size={IconButtonSize.SM}
              onClick={() => setShowFilters(true)}
              HugeIcon={FilterIcon}
            />
            <PropertyManagementSystemSync
              isSyncing={isSyncing}
              handleRequestSync={handleRequestSync}
              lastSuccessSyncedAt={syncData?.last_successful_sync}
            />
            <Show if={type.isAddressValidationEnabled}>
              <div className="hidden shrink-0 items-center md:flex">
                <ValidateAllAddressesButton />
              </div>
            </Show>
            <div className="ml-auto md:flex shrink-0 gap-2 md:w-auto w-full hidden">
              <LayoutButtons onPropertyAdded={refetch} />
            </div>
          </div>

          <Show if={activeFilterCount > 0}>
            <div className="flex flex-wrap gap-2 mt-3">
              {filters.states?.map((state) => (
                <FilterBadge
                  key={state}
                  filter={state}
                  onRemove={() => removeFilter(removeState)(state)}
                />
              ))}
              {filters.propertyTypes?.map((kind) => (
                <FilterBadge
                  key={kind}
                  filter={propertyKindLabel[kind] ?? kind}
                  onRemove={() => removeFilter(removePropertyType)(kind)}
                />
              ))}
              {filters.screeningTemplates?.map((t) => (
                <FilterBadge
                  key={t.id}
                  filter={t.name}
                  onRemove={() => removeFilter(removeScreeningTemplate)(t.id)}
                />
              ))}
              {filters.listingTemplates?.map((t) => (
                <FilterBadge
                  key={t.id}
                  filter={t.name}
                  onRemove={() => removeFilter(removeListingTemplate)(t.id)}
                />
              ))}
            </div>
          </Show>
        </div>

        <PropertiesTable
          data={data?.items || []}
          errorMessage={filterErrorMessage}
          setSelectedPropertyId={setSelectedPropertyId}
          hasSearchParams={hasAnyFilter}
          setShowDeletePropertyModal={setShowDeletePropertyModal}
          isLoading={isFetching}
          perPageRows={DEFAULT_PER_PAGE}
          viewPortHeightOffset={120}
          pagination={
            <div className="ml-auto flex md:max-w-[calc(100vw-106px)]">
              <Pagination
                className="md:justify-end justify-center"
                total={data?.page_count || 0}
                current={page}
                onChange={(p) => setPage(p)}
              />
            </div>
          }
        />
        <Show if={type.isAddressValidationEnabled}>
          <div className="mt-6">
            <AddressValidationIssuesCard />
          </div>
        </Show>
        <DeletePropertyModal
          opened={showDeletePropertyModal}
          onClose={() => setShowDeletePropertyModal(false)}
          propertyId={selectedPropertyId || ""}
          onSuccess={() => {
            refetch();
          }}
        />

        <PropertiesFiltersDrawer
          open={showFilters}
          onClose={() => setShowFilters(false)}
          filters={filters}
          onApply={applyFilters}
        />
      </div>
    </Layout>
  );
};
